import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, MessageSquare, ArrowLeft, Send, Trash2, Sparkles, Search, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORUM_CATEGORIES = ["Alle", "Allgemein", "KI-Tools", "Coding", "Business", "Off-Topic"];
const ACCENT = "#00e5ff";

const CAT_COLORS = {
  "Allgemein": "#00e5ff",
  "KI-Tools":  "#a855f7",
  "Coding":    "#2ecc71",
  "Business":  "#f39c12",
  "Off-Topic": "#ec4899",
};

const inputCls = "mt-2 bg-black border-white/10 text-white rounded-none h-11 text-[14px] focus:border-[#00e5ff]/60 focus:ring-0 transition-colors";

function FieldLabel({ children }) {
  return <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">{children}</Label>;
}

export default function ForumPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "Allgemein" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = (searchTerm || "").trim();
    if (q.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/forum/search`, { params: { q }, withCredentials: true });
        setSearchResults(data);
      } catch (err) {
        console.error("Forum search failed:", err);
        setSearchResults({ query: q, threads: [], replies: [] });
      } finally {
        setSearching(false);
      }
    }, 280); // debounce
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const fetchThreads = useCallback(async () => {
    try {
      const params = activeCategory !== "Alle" ? { category: activeCategory } : {};
      const { data } = await axios.get(`${API}/forum/threads`, { params, withCredentials: true });
      setThreads(data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch threads:", err);
      setError("Threads konnten nicht geladen werden.");
    }
  }, [activeCategory]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const openThread = useCallback(async (threadId) => {
    setSelectedThread(threadId);
    try {
      const { data } = await axios.get(`${API}/forum/threads/${threadId}`, { withCredentials: true });
      setThreadDetail(data);
      setError("");
    } catch (err) {
      console.error("Failed to load thread:", err);
      setError("Thread konnte nicht geladen werden.");
    }
  }, []);

  const handleCreateThread = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/forum/threads`, form, { withCredentials: true });
      setCreateOpen(false);
      setForm({ title: "", content: "", category: "Allgemein" });
      fetchThreads();
    } catch (err) {
      console.error("Failed to create thread:", err);
      setError("Thread konnte nicht erstellt werden.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = useCallback(async () => {
    if (!replyText.trim() || !selectedThread) return;
    try {
      const { data } = await axios.post(`${API}/forum/threads/${selectedThread}/replies`, { content: replyText }, { withCredentials: true });
      setThreadDetail((prev) => prev ? { ...prev, replies: [...prev.replies, data] } : prev);
      setReplyText("");
      fetchThreads();
      setError("");
    } catch (err) {
      console.error("Failed to post reply:", err);
      setError("Antwort konnte nicht gepostet werden.");
    }
  }, [replyText, selectedThread, fetchThreads]);

  const handleDeleteThread = useCallback(async (threadId) => {
    try {
      await axios.delete(`${API}/forum/threads/${threadId}`, { withCredentials: true });
      setSelectedThread(null);
      setThreadDetail(null);
      fetchThreads();
      setError("");
    } catch (err) {
      console.error("Failed to delete thread:", err);
      setError("Thread konnte nicht gelöscht werden.");
    }
  }, [fetchThreads]);

  const formatDate = (iso) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  // ---- Thread Detail ----
  if (selectedThread && threadDetail) {
    const isOwner = user?._id === threadDetail.author_id || user?.id === threadDetail.author_id || user?.role === "admin";
    const catColor = CAT_COLORS[threadDetail.category] || ACCENT;
    return (
      <div className="min-h-screen bg-black text-white antialiased relative overflow-x-hidden">
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 right-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.05) 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10">
          <Navbar />
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
            <button onClick={() => { setSelectedThread(null); setThreadDetail(null); }} className="flex items-center gap-2 text-[13px] text-white/45 hover:text-[#00e5ff] transition-colors mb-5 font-outfit tracking-wide" data-testid="back-to-threads">
              <ArrowLeft size={15} />Zurück zum Forum
            </button>
            <div className="relative mb-6" data-testid="thread-detail">
              <div className="bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.07] p-6 sm:p-7" style={{ borderLeft: `3px solid ${catColor}` }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold border mb-3" style={{ color: catColor, borderColor: `${catColor}40`, background: `${catColor}0d` }}>
                      {threadDetail.category}
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-outfit font-bold text-white tracking-tight mb-3 leading-tight">{threadDetail.title}</h2>
                    <p className="text-[15px] text-white/70 whitespace-pre-wrap mb-4 leading-relaxed">{threadDetail.content}</p>
                    <div className="flex items-center gap-2 text-[12px] text-white/35">
                      <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] uppercase font-semibold text-white/60">{threadDetail.author_name?.charAt(0) || "?"}</span>
                      {threadDetail.author_name} · {formatDate(threadDetail.created_at)}
                    </div>
                  </div>
                  {isOwner && (
                    <button onClick={() => handleDeleteThread(threadDetail.id)} data-testid="delete-thread-btn" className="text-red-400/60 hover:text-red-400 p-2 shrink-0"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-3 text-[11px] uppercase tracking-[0.28em] text-white/40 font-outfit font-semibold flex items-center gap-2"><span className="w-6 h-px bg-white/20" />Antworten · {threadDetail.replies?.length || 0}</div>
            <div className="space-y-3 mb-6" data-testid="thread-replies">
              {threadDetail.replies.map((r) => (
                <div key={r.id} className="border-l-2 border-white/[0.08] pl-4 py-2" data-testid={`reply-${r.id}`}>
                  <p className="text-[14px] text-white/80 leading-relaxed">{r.content}</p>
                  <p className="text-[11px] text-white/35 mt-1.5 flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center text-[8px] uppercase font-semibold text-white/60">{r.author_name?.charAt(0) || "?"}</span>
                    {r.author_name} · {formatDate(r.created_at)}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2" data-testid="reply-form">
              <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Antwort schreiben..." data-testid="reply-input" className="flex-1 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 h-11 text-[14px]" onKeyDown={(e) => e.key === "Enter" && handleReply()} />
              <Button onClick={handleReply} data-testid="submit-reply-btn" className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none h-11 px-4 transition-all"><Send size={15} /></Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Thread List ----
  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.06) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-px bg-[#00e5ff]" />
                <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">Community Forum</span>
              </div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white mb-3" data-testid="forum-heading">
                <span className="text-[#00e5ff]">Diskussionen</span>
              </h1>
              <p className="text-base sm:text-lg text-white/50">{threads.length} Threads · Tiefgang, kein Small-Talk</p>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-thread-btn" className="group/btn relative overflow-hidden bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none px-5 h-11 font-outfit font-semibold text-[13px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] shrink-0">
                  <Plus size={15} className="mr-1.5" />Neues Thema
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none" data-testid="create-thread-dialog">
                <div className="relative">
                  <div className="absolute -inset-px pointer-events-none" style={{ background: `linear-gradient(135deg, ${ACCENT}40 0%, transparent 50%, ${ACCENT}20 100%)` }} />
                  <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
                    <DialogHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-px bg-[#00e5ff]/60" />
                        <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">Erstellen</span>
                      </div>
                      <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">Neues Thema</DialogTitle>
                      <DialogDescription className="text-[13px] text-white/45 mt-1">Starte eine Diskussion. Bleib konkret und teile deinen Kontext.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateThread} className="space-y-4 mt-5">
                      <div>
                        <FieldLabel>Kategorie</FieldLabel>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                          <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                            {FORUM_CATEGORIES.filter((c) => c !== "Alle").map((c) => (
                              <SelectItem key={c} value={c} className="rounded-none hover:bg-white/5 focus:bg-white/5 focus:text-white">
                                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: CAT_COLORS[c] || ACCENT }} />{c}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <FieldLabel>Titel *</FieldLabel>
                        <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="thread-title-input" className={inputCls} />
                      </div>
                      <div>
                        <FieldLabel>Inhalt *</FieldLabel>
                        <Textarea required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="thread-content-input" className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[120px] text-[14px]" />
                      </div>
                      <Button type="submit" disabled={isSubmitting} data-testid="submit-thread-btn" className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all">
                        {isSubmitting ? "..." : "Thema erstellen"}
                      </Button>
                    </form>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Category Pills */}
          <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1 -mx-1 px-1" data-testid="forum-categories">
            {FORUM_CATEGORIES.map((c) => {
              const isActive = activeCategory === c;
              const color = c === "Alle" ? ACCENT : (CAT_COLORS[c] || ACCENT);
              return (
                <button key={c} onClick={() => setActiveCategory(c)} data-testid={`forum-cat-${c.toLowerCase()}`}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-[12px] uppercase tracking-[0.18em] font-outfit font-medium whitespace-nowrap transition-all duration-300 border"
                  style={{ color: isActive ? color : "rgba(255,255,255,0.5)", borderColor: isActive ? `${color}55` : "rgba(255,255,255,0.08)", background: isActive ? `${color}0d` : "transparent" }}>
                  {c}
                </button>
              );
            })}
          </div>

          {/* Search bar */}
          <div className="relative mb-4" data-testid="forum-search-wrap">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Forum durchsuchen (min. 2 Zeichen)..."
              data-testid="forum-search-input"
              className="w-full h-11 pl-9 pr-9 bg-black border border-white/10 text-white text-[14px] font-outfit placeholder:text-white/30 focus:outline-none focus:border-[#00e5ff]/60 rounded-none transition-colors"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} data-testid="forum-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Threads list */}
          <div className="bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.06]" data-testid="threads-list">
            {searchResults ? (
              <>
                <div className="px-5 sm:px-6 py-3 border-b border-white/[0.06] flex items-center justify-between gap-3 bg-white/[0.015]" data-testid="forum-search-meta">
                  <span className="text-[12px] text-white/55 font-outfit font-medium">
                    {searching
                      ? "Suche läuft…"
                      : <>
                          <b className="text-white">{searchResults.threads.length}</b> Thread{searchResults.threads.length === 1 ? "" : "s"} · <b className="text-white">{searchResults.replies.length}</b> Antwort{searchResults.replies.length === 1 ? "" : "en"} für „<span className="text-[#00e5ff]">{searchResults.query}</span>"
                        </>
                    }
                  </span>
                </div>
                {searchResults.threads.length === 0 && searchResults.replies.length === 0 && !searching && (
                  <div className="text-center py-16" data-testid="forum-search-no-results">
                    <Search size={24} className="text-white/25 mx-auto mb-3" strokeWidth={1.3} />
                    <p className="text-white/70 text-[15px] font-outfit font-semibold">Keine Treffer</p>
                    <p className="text-[13px] text-white/40 mt-1">Versuch's mit anderen Stichwörtern.</p>
                  </div>
                )}
                {searchResults.threads.map((t, idx) => {
                  const color = CAT_COLORS[t.category] || ACCENT;
                  return (
                    <div key={`t-${t.id}`} onClick={() => openThread(t.id)} data-testid={`forum-search-thread-${t.id}`}
                      className={`group px-5 sm:px-6 py-5 cursor-pointer hover:bg-white/[0.025] transition-all duration-200 ${idx < searchResults.threads.length - 1 || searchResults.replies.length > 0 ? "border-b border-white/[0.05]" : ""}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold border" style={{ color, borderColor: `${color}40`, background: `${color}0d` }}>
                          {t.category}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] font-outfit font-bold text-white/35">Thread</span>
                      </div>
                      <h3 className="text-[16px] sm:text-[17px] font-outfit font-semibold text-white tracking-tight leading-snug mb-1 line-clamp-2">{t.title}</h3>
                      {t.snippet && <p className="text-[13px] text-white/50 line-clamp-2 leading-relaxed mb-1.5">{t.snippet}</p>}
                      <p className="text-[12px] text-white/40 flex items-center gap-1.5">
                        <span className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center text-[8px] uppercase font-semibold text-white/60">{t.author_name?.charAt(0) || "?"}</span>
                        {t.author_name} · {formatDate(t.last_activity || t.created_at)} · {t.reply_count} {t.reply_count === 1 ? "Antwort" : "Antworten"}
                      </p>
                    </div>
                  );
                })}
                {searchResults.replies.map((r, idx) => {
                  const color = CAT_COLORS[r.thread_category] || ACCENT;
                  return (
                    <div key={`r-${r.id}`} onClick={() => openThread(r.thread_id)} data-testid={`forum-search-reply-${r.id}`}
                      className={`group px-5 sm:px-6 py-5 cursor-pointer hover:bg-white/[0.025] transition-all duration-200 ${idx < searchResults.replies.length - 1 ? "border-b border-white/[0.05]" : ""}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold border" style={{ color, borderColor: `${color}40`, background: `${color}0d` }}>
                          {r.thread_category}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] font-outfit font-bold text-[#a855f7]/80">Antwort in</span>
                        <span className="text-[13px] text-white/75 font-outfit font-semibold truncate">{r.thread_title}</span>
                      </div>
                      <p className="text-[14px] text-white/70 line-clamp-2 leading-relaxed border-l-2 border-[#a855f7]/40 pl-3">{r.snippet}</p>
                      <p className="text-[11px] text-white/35 mt-2 flex items-center gap-1.5">
                        <span className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center text-[8px] uppercase font-semibold text-white/60">{r.author_name?.charAt(0) || "?"}</span>
                        {r.author_name} · {formatDate(r.created_at)}
                      </p>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {threads.length === 0 && (
                  <div className="text-center py-20">
                    <Sparkles size={28} className="text-[#00e5ff]/50 mx-auto mb-3" strokeWidth={1.3} />
                    <p className="text-white/80 text-lg font-outfit font-semibold">Noch keine Diskussionen</p>
                    <p className="text-[14px] text-white/40 mt-1.5">Starte die erste — bleib konkret.</p>
                  </div>
                )}
                {threads.map((t, idx) => {
                  const color = CAT_COLORS[t.category] || ACCENT;
                  return (
                    <div key={t.id} onClick={() => openThread(t.id)} data-testid={`thread-row-${t.id}`}
                      className={`group px-5 sm:px-6 py-5 cursor-pointer hover:bg-white/[0.025] transition-all duration-200 ${idx < threads.length - 1 ? "border-b border-white/[0.05]" : ""}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold border" style={{ color, borderColor: `${color}40`, background: `${color}0d` }}>
                              {t.category}
                            </span>
                          </div>
                          <h3 className="text-[16px] sm:text-[17px] font-outfit font-semibold text-white tracking-tight leading-snug mb-1.5 group-hover:text-white line-clamp-2">{t.title}</h3>
                          <p className="text-[12px] text-white/40 flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center text-[8px] uppercase font-semibold text-white/60">{t.author_name?.charAt(0) || "?"}</span>
                            {t.author_name} · {formatDate(t.created_at)}
                          </p>
                        </div>
                        <div className="flex flex-col items-center gap-0.5 text-white/45 shrink-0 pt-0.5">
                          <MessageSquare size={16} />
                          <span className="text-[13px] font-outfit font-semibold">{t.reply_count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
